import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Printer, RotateCcw } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  todayInAccountZone,
  validateRefund,
} from "../lib/payment";
import { isAccountAdmin } from "../lib/people";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";

const STATUS_TONE: Record<string, "emerald" | "amber" | "slate"> = {
  issued: "amber",
  refunded: "emerald",
  void: "slate",
};

/** One credit note in full, with its audit history and its two reversals. */
export function CreditNoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [mode, setMode] = useState<"none" | "void" | "refund">("none");

  const query = useQuery({
    queryKey: ["credit-note", id],
    queryFn: ({ signal }) => api.creditNote(id!, signal),
    enabled: Boolean(id),
  });
  const history = useQuery({
    queryKey: ["credit-note", id, "history"],
    queryFn: ({ signal }) => api.creditNoteHistory(id!, signal),
    enabled: Boolean(id),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["credit-note", id] });
    void queryClient.invalidateQueries({ queryKey: ["credit-notes"] });
    void queryClient.invalidateQueries({ queryKey: ["billing"] });
  };

  const voidNote = useMutation({
    mutationFn: (reason: string) => api.voidCreditNote(id!, reason),
    onSuccess: () => {
      setMode("none");
      invalidate();
    },
  });
  const refund = useMutation({
    mutationFn: (body: {
      amount: string;
      method: string;
      reference?: string;
      payment_date?: string | null;
    }) => api.refundCreditNote(id!, body),
    onSuccess: () => {
      setMode("none");
      invalidate();
    },
  });

  if (query.isPending) {
    return (
      <>
        <BackLink />
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      </>
    );
  }
  if (query.isError || !query.data) {
    return (
      <>
        <BackLink />
        <Card>
          <EmptyState
            title="Credit note not found"
            body={
              query.error instanceof ApiError
                ? query.error.message
                : "It may belong to another account."
            }
          />
        </Card>
      </>
    );
  }

  const note = query.data;
  const canReverse = isAccountAdmin(user?.role);
  const refundable =
    Number(note.total_amount?.amount ?? 0) - Number(note.amount_refunded?.amount ?? 0);
  const active = note.status === "issued";

  return (
    <>
      <div className="print:hidden">
        <BackLink />
        <PageHeader
          title={note.number || "Credit note"}
          subtitle={note.contact_name || undefined}
          actions={
            <>
              <Badge tone={STATUS_TONE[note.status] ?? "slate"}>
                <span className="capitalize">{note.status}</span>
              </Badge>
              <Button variant="secondary" onClick={() => window.print()}>
                <Printer size={14} /> Print
              </Button>
              {canReverse && active ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => setMode(mode === "void" ? "none" : "void")}
                  >
                    <Ban size={14} /> Void
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setMode(mode === "refund" ? "none" : "refund")}
                    disabled={refundable <= 0}
                  >
                    <RotateCcw size={14} /> Refund
                  </Button>
                </>
              ) : null}
            </>
          }
        />
      </div>

      <div className="print:hidden">
        {mode === "void" ? (
          <VoidForm
            pending={voidNote.isPending}
            error={voidNote.error}
            onCancel={() => setMode("none")}
            onSubmit={(reason) => voidNote.mutate(reason)}
          />
        ) : null}
        {mode === "refund" ? (
          <RefundForm
            max={refundable.toFixed(2)}
            pending={refund.isPending}
            error={refund.error}
            onCancel={() => setMode("none")}
            onSubmit={(body) => refund.mutate(body)}
          />
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card padded={false}>
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold">Credited lines</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2.5">Description</th>
                  <th className="px-4 py-2.5 text-right">Qty</th>
                  <th className="px-4 py-2.5 text-right">Unit price</th>
                  <th className="px-4 py-2.5 text-right">Tax</th>
                  <th className="px-5 py-2.5 text-right">Line total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {note.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-3 text-sm text-slate-800">{item.description || "—"}</td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                      {item.quantity}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                      {formatMoney(item.unit_price)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                      {formatMoney(item.tax_amount)}
                    </td>
                    <td className="px-5 py-3 text-right text-sm tabular-nums text-slate-900">
                      {formatMoney(item.line_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card padded={false}>
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold">Audit history</h2>
            </div>
            {history.isPending ? (
              <div className="p-5">
                <Skeleton className="h-20 w-full" />
              </div>
            ) : (history.data?.results.length ?? 0) === 0 ? (
              <EmptyState title="No events" body="Nothing has been recorded against this note." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {history.data?.results.map((event, index) => (
                  <li key={`${event.event_type}-${index}`} className="px-5 py-3">
                    <p className="text-sm text-slate-800">{event.event_type}</p>
                    <p className="text-xs text-slate-500">
                      {event.actor_name || "System"} · {formatDateTime(event.occurred_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h2 className="mb-3 text-sm font-semibold">Customer</h2>
            <p className="text-sm font-medium text-slate-900">{note.contact_name || "—"}</p>
            <Link
              to={`/customers/contacts/${note.contact}`}
              className="mt-2 inline-flex text-xs font-medium text-slate-600 underline hover:text-slate-900"
            >
              View contact
            </Link>
          </Card>

          <Card>
            <h2 className="mb-4 text-sm font-semibold">Credit note</h2>
            <dl className="space-y-3 text-sm">
              <Row label="Number" value={note.number || "—"} />
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-slate-500">Original invoice</dt>
                <dd className="text-right text-slate-800">
                  <Link
                    to={`/billing/invoices/${note.invoice}`}
                    className="font-medium underline hover:text-slate-900"
                  >
                    {note.invoice_number || "View invoice"}
                  </Link>
                </dd>
              </div>
              <Row label="Reason" value={note.reason.replace(/_/g, " ")} />
              <Row label="Issued" value={formatDate(note.issue_date)} />
              {note.issued_at ? (
                <Row label="Issued at" value={formatDateTime(note.issued_at)} />
              ) : null}
              {note.voided_at ? (
                <Row label="Voided at" value={formatDateTime(note.voided_at)} />
              ) : null}
              {note.void_reason ? <Row label="Void reason" value={note.void_reason} /> : null}
            </dl>
          </Card>
          <Card>
            <h2 className="mb-4 text-sm font-semibold">Totals</h2>
            <dl className="space-y-3 text-sm">
              <Row label="Subtotal" value={formatMoney(note.subtotal)} />
              <Row label="Discount" value={formatMoney(note.discount_amount)} />
              <Row label="Tax" value={formatMoney(note.tax_amount)} />
              <div className="border-t border-slate-100 pt-3">
                <Row label="Total" value={formatMoney(note.total_amount)} strong />
              </div>
              <Row label="Refunded" value={formatMoney(note.amount_refunded)} />
            </dl>
          </Card>
          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold">Refunds</h2>
              <span className="text-xs text-slate-500">
                {(note.refunds?.length ?? 0) === 0
                  ? "None recorded"
                  : `${note.refunds?.length} recorded`}
              </span>
            </div>
            {(note.refunds?.length ?? 0) === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">
                Issuing a credit note does not move money. A refund appears here only once it
                has been recorded as an outbound payment.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {note.refunds?.map((refund) => (
                  <li key={refund.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium tabular-nums text-slate-900">
                        {refund.receipt_number || "Refund"}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(refund.payment_date)} · {refund.method}
                        {refund.reference ? ` · ${refund.reference}` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium tabular-nums text-rose-700">
                        −{formatMoney(refund.amount)}
                      </p>
                      <Link
                        to={`/billing/payments/${refund.id}/receipt`}
                        className="text-xs font-medium text-slate-600 hover:text-slate-900"
                      >
                        Receipt
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          {note.details ? (
            <Card>
              <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                Details
              </h2>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{note.details}</p>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

function VoidForm({
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <Card className="mb-4">
      <h2 className="mb-3 text-sm font-semibold">Void this credit note</h2>
      <p className="mb-3 text-sm text-slate-600">
        Voiding puts the credited money back onto the invoice. A refunded note cannot be
        voided.
      </p>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why is this being voided?"
        className={INPUT}
      />
      {error ? (
        <p role="alert" className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error instanceof ApiError ? error.message : "Could not void the credit note."}
        </p>
      ) : null}
      <div className="mt-3 flex gap-2">
        <Button variant="primary" disabled={pending || !reason.trim()} onClick={() => onSubmit(reason)}>
          {pending ? "Voiding…" : "Void credit note"}
        </Button>
        <Button onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

function RefundForm({
  max,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  max: string;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (body: {
    amount: string;
    method: string;
    reference: string;
    payment_date: string;
  }) => void;
}) {
  const timeZone = useAccountTimeZone();
  const [amount, setAmount] = useState(max);
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => todayInAccountZone(timeZone));
  const errors = validateRefund({
    amount,
    paymentDate,
    method,
    reference,
    outstanding: 0,
    refundable: Number(max),
  });
  const valid = Object.keys(errors).length === 0;

  return (
    <Card className="mb-4">
      <h2 className="mb-3 text-sm font-semibold">Refund this credit note</h2>
      <p className="mb-3 text-sm text-slate-600">
        Up to {max} can still be refunded. A refund records an outbound payment.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Amount
          </span>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={INPUT}
          />
          {errors.amount ? <FieldError>{errors.amount}</FieldError> : null}
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Refund date
          </span>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className={INPUT}
          />
          {errors.payment_date ? <FieldError>{errors.payment_date}</FieldError> : null}
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Method
          </span>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className={INPUT}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Reference
          </span>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={method === "cash" ? "Optional" : "UPI / card / transaction id"}
            className={INPUT}
          />
          {errors.reference ? <FieldError>{errors.reference}</FieldError> : null}
        </label>
      </div>
      {error ? (
        <p role="alert" className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error instanceof ApiError ? error.message : "Could not refund the credit note."}
        </p>
      ) : null}
      <div className="mt-3 flex gap-2">
        <Button
          variant="primary"
          disabled={pending || !valid}
          onClick={() =>
            onSubmit({
              amount: amount.trim(),
              method,
              reference: reference.trim(),
              payment_date: paymentDate,
            })
          }
        >
          {pending ? "Refunding…" : "Record refund"}
        </Button>
        <Button onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

function FieldError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="mt-1 text-xs text-rose-600">
      {children}
    </p>
  );
}

const INPUT =
  "w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={strong ? "font-semibold text-slate-900" : "text-slate-800"}>{value}</dd>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/billing/credit-notes"
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> All credit notes
    </Link>
  );
}
